import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppLayout } from "./components/layout/AppLayout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { InvestorDashboardPage } from "./pages/InvestorDashboardPage";
import { TeknisiDashboardPage } from "./pages/TeknisiDashboardPage";
import { PenarikDashboardPage } from "./pages/PenarikDashboardPage";
import { JaringanMikrotikPage } from "./pages/JaringanMikrotikPage";
import { JaringanOltPage } from "./pages/JaringanOltPage";
import { JaringanMapsPage } from "./pages/JaringanMapsPage";
import { DataPelangganPage } from "./pages/DataPelangganPage";
import { KeuanganTagihanPage } from "./pages/KeuanganTagihanPage";
import { KeuanganPendapatanLainPage } from "./pages/KeuanganPendapatanLainPage";
import { KasBankPage } from "./pages/KasBankPage";
import { MasterAsetPage } from "./pages/MasterAsetPage";
import { KeuanganPengeluaranPage } from "./pages/KeuanganPengeluaranPage";
import { KeuanganGajiPage } from "./pages/KeuanganGajiPage";
import { KeuanganPajakPage } from "./pages/KeuanganPajakPage";
import { KeuanganDanaCadanganPage } from "./pages/KeuanganDanaCadanganPage";
import { KeuanganOperasionalPage } from "./pages/KeuanganOperasionalPage";
import { LaporanPage } from "./pages/LaporanPage";
import { MasterDataPage } from "./pages/MasterDataPage";
import { MasterKaryawanPage } from "./pages/MasterKaryawanPage";
import { MasterPaketPage } from "./pages/MasterPaketPage";
import { MasterWilayahPage } from "./pages/MasterWilayahPage";
import { MasterOdpPage } from "./pages/MasterOdpPage";
import { MasterMitraPage } from "./pages/MasterMitraPage";
import { MasterPenarikPage } from "./pages/MasterPenarikPage";
import { PengaturanPerusahaanPage } from "./pages/PengaturanPerusahaanPage";
import { PengaturanAplikasiPage } from "./pages/PengaturanAplikasiPage";
import { PengaturanPenggunaPage } from "./pages/PengaturanPenggunaPage";
import { PengaturanDataPage } from "./pages/PengaturanDataPage";
import { PengaturanLegalPage } from "./pages/PengaturanLegalPage";
import { DownloadApkPage } from "./pages/DownloadApkPage";
import { PortalAuthProvider } from "./portal/PortalAuthContext";
import { PortalLoginPage } from "./portal/PortalLoginPage";
import { PortalLayout, PortalProtected } from "./portal/PortalLayout";
import { PortalHomePage } from "./portal/PortalHomePage";
import { PortalTagihanPage } from "./portal/PortalTagihanPage";
import { PortalUpgradePage } from "./portal/PortalUpgradePage";
import { PortalSpeedtestPage } from "./portal/PortalSpeedtestPage";
import { PortalAkunPage } from "./portal/PortalAkunPage";
import { TentangKamiPage, KebijakanPrivasiPage, SyaratKetentuanPage, KontakPage } from "./pages/legal/LegalPages";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070c18] flex items-center justify-center text-cyan-400 font-mono text-sm">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <span>Memuat Sistem Billing ISP...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.role === "investor") return <InvestorDashboardPage />;
  if (user?.role === "teknisi") return <TeknisiDashboardPage />;
  if (user?.role === "penarik") return <PenarikDashboardPage />;
  return <DashboardPage />;
};

function App() {
  return (
    <AuthProvider>
      <PortalAuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Halaman statis publik (untuk verifikasi Payment Gateway & pengunjung) */}
          <Route path="/tentang-kami" element={<TentangKamiPage />} />
          <Route path="/kebijakan-privasi" element={<KebijakanPrivasiPage />} />
          <Route path="/syarat-ketentuan" element={<SyaratKetentuanPage />} />
          <Route path="/kontak" element={<KontakPage />} />


          {/* Portal Pelanggan */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal" element={<PortalProtected><PortalLayout /></PortalProtected>}>
            <Route index element={<PortalHomePage />} />
            <Route path="tagihan" element={<PortalTagihanPage />} />
            <Route path="upgrade" element={<PortalUpgradePage />} />
            <Route path="speedtest" element={<PortalSpeedtestPage />} />
            <Route path="akun" element={<PortalAkunPage />} />
          </Route>

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardRouter />} />
            
            {/* Jaringan Dropdown */}
            <Route path="jaringan/mikrotik" element={<JaringanMikrotikPage />} />
            <Route path="jaringan/olt" element={<JaringanOltPage />} />
            <Route path="jaringan/maps" element={<JaringanMapsPage />} />
            
            {/* Data Pelanggan */}
            <Route path="pelanggan" element={<DataPelangganPage />} />
            
            {/* Keuangan Dropdown */}
            <Route path="keuangan/tagihan" element={<KeuanganTagihanPage />} />
            <Route path="keuangan/pendapatan-lain" element={<KeuanganPendapatanLainPage />} />
            <Route path="keuangan/kas" element={<KasBankPage />} />
            <Route path="keuangan/aset" element={<MasterAsetPage />} />
            <Route path="keuangan/pengeluaran" element={<KeuanganPengeluaranPage />} />
            <Route path="keuangan/gaji" element={<KeuanganGajiPage />} />
            <Route path="keuangan/pajak" element={<KeuanganPajakPage />} />
            <Route path="keuangan/dana-cadangan" element={<KeuanganDanaCadanganPage />} />
            <Route path="keuangan/operasional" element={<KeuanganOperasionalPage />} />
            
            {/* Laporan */}
            <Route path="laporan" element={<LaporanPage />} />
            
            {/* Master Data Dropdown */}
            <Route path="master-data" element={<MasterDataPage />} />
            <Route path="master/karyawan" element={<MasterKaryawanPage />} />
            <Route path="master/paket" element={<MasterPaketPage />} />
            <Route path="master/wilayah" element={<MasterWilayahPage />} />
            <Route path="master/odp" element={<MasterOdpPage />} />
            <Route path="master/mitra" element={<MasterMitraPage />} />
            <Route path="master/penarik" element={<MasterPenarikPage />} />
            
            {/* Pengaturan Dropdown */}
            <Route path="pengaturan/perusahaan" element={<PengaturanPerusahaanPage />} />
            <Route path="pengaturan/aplikasi" element={<PengaturanAplikasiPage />} />
            <Route path="pengaturan/pengguna" element={<PengaturanPenggunaPage />} />
            <Route path="pengaturan/data" element={<PengaturanDataPage />} />
            <Route path="pengaturan/legal" element={<PengaturanLegalPage />} />
            <Route path="pengaturan/download-apk" element={<DownloadApkPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </PortalAuthProvider>
    </AuthProvider>
  );
}

export default App;
